"use client";

import Cal, { type EmbedEvent, getCalApi } from "@calcom/embed-react";
import { useTheme } from "next-themes";
import { useEffect, useEffectEvent } from "react";
import { cn } from "@/lib/utils";

/** Shared namespace so getCalApi and <Cal> target the same embed instance. */
const CAL_NAMESPACE = "book-a-demo";

/**
 * Inline cal.com scheduler, themed to match the site (light/dark).
 * `calLink` is the path after cal.com/ (e.g. "team/helios/product-demo").
 * `onBookingSuccess` fires once per completed booking (cal.com's
 * `bookingSuccessfulV2`; the booking may still await host confirmation).
 *
 * UI-only options (theme, layout, hideEventTypeDetails) must be applied via
 * cal("ui", ...), not the <Cal config> prop, which only accepts prefill and
 * iframe attributes.
 */
export function CalEmbed({
  calLink,
  className,
  onBookingSuccess,
}: {
  calLink: string;
  className?: string;
  onBookingSuccess?: () => void;
}) {
  const { resolvedTheme } = useTheme();
  const theme = resolvedTheme === "dark" ? "dark" : "light";
  const handleBookingSuccess = useEffectEvent(() => onBookingSuccess?.());

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE });
      if (cancelled) return;

      cal("ui", {
        theme,
        layout: "month_view",
        hideEventTypeDetails: true,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [theme]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE });
      if (cancelled) return;

      const callback = (_e: EmbedEvent<"bookingSuccessfulV2">) => handleBookingSuccess();
      cal("on", { action: "bookingSuccessfulV2", callback });
      unsubscribe = () => cal("off", { action: "bookingSuccessfulV2", callback });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return <Cal namespace={CAL_NAMESPACE} calLink={calLink} className={cn("w-full", className)} />;
}
